import {NavLink, useMantineColorScheme} from '@mantine/core'

import {useLocation, useNavigate} from 'react-router-dom'
import useIsAuthenticated from "react-auth-kit/hooks/useIsAuthenticated";
import {HasAction} from "components/Auth/HasAction";
import {ADMIN_HUB_ACTIONS, GM_HUB_ACTIONS, TRANSACTION_MANAGER_HUB_ACTIONS, TECH_HUB_ACTIONS} from "lib/hubAccess";

/**
 * Props for the Sidenav component.
 * @property opened - Whether the sidenav is currently expanded/visible.
 * @property setSidenavOpened - Callback to update the sidenav open state.
 * @property handleLogout - Async callback invoked when the user requests logout.
 */
interface SidenavProps {
    opened: boolean;
    setSidenavOpened: (value: boolean) => void;
    handleLogout: () => Promise<void>;
}

function Sidenav(props: SidenavProps) {
    const location = useLocation()
    const isAuthenticated = useIsAuthenticated();

    return (<>
            {/* Public navigation - visible to all */}
            <NavItem label={"Home"} path={"/"} location={location.pathname}/>
            <NavItem label={"Teams"} path={"/teams"} location={location.pathname}/>
            <NavItem label={"Franchises"} path={"/franchises"} location={location.pathname}/>
            <NavItem label={"Users"} path={"/users"} location={location.pathname}/>
            <NavItem label={"Matches"} path={"/matches"} location={location.pathname}/>

            {/* Authenticated user features */}
            {isAuthenticated && (
                <NavItem label={"Combine Queue"} path={"/combines/queue"} location={location.pathname}/>
            )}

            {/* Action-restricted hubs - visible to users with relevant entity permissions */}
            <HasAction action={[...ADMIN_HUB_ACTIONS]}>
                <NavItem label={"Admin Hub"} path={"/admin-hub"} location={location.pathname}/>
            </HasAction>
            <HasAction action={[...GM_HUB_ACTIONS]}>
                <NavItem label={"GM Hub"} path={"/gm-hub"} location={location.pathname}/>
            </HasAction>
            <HasAction action={[...TRANSACTION_MANAGER_HUB_ACTIONS]}>
                <NavItem label={"Transaction Manager"} path={"/tm-hub"} location={location.pathname}/>
            </HasAction>
            <HasAction action={[...TECH_HUB_ACTIONS]}>
                <NavItem label={"Tech Hub"} path={"/tech-hub"} location={location.pathname}/>
            </HasAction>
        </>
    )
}

export default Sidenav

function NavItem(
    {label, path, icon, location, onClick}:
    { label: string, path: string, icon?: string, location: string, onClick?: () => void }) {

    const navigate = useNavigate();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const c = location.split('/')[1];
    const b = path.split('/')[1];
    const isActive = c === b;

    return <NavLink
        label={label}
        py={8}
        onClick={() => {
            if (onClick) onClick();
            navigate(path);
        }}
        active={isActive}
        variant={isActive ? "filled" : "subtle"}
        color={isDark ? "blue" : undefined}
    />

}
